import React, { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import { useApp } from '../context/AppContext';
import DashboardPage, { TurnoverInfo } from './DashboardPage';
import {
  calculatePublicOrderPayment,
  getPaymentProducts,
  getPublicOrderPaymentLimits,
  PaymentCalculation,
  PaymentLimits,
} from '../api/api';
import { clientErrorMessage } from '../utils/clientError';
import { buildPaymentNetworkOptions, PaymentNetworkOption } from '../utils/paymentProducts';

const featureStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};

const featureTextStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  color: 'var(--text-muted)',
};

export default function LandingPage() {
  const { userAuth, settings, refreshSettings } = useApp();
  const [params] = useSearchParams();
  const [rub, setRub] = useState('');
  const [paymentOptions, setPaymentOptions] = useState<PaymentNetworkOption[]>([]);
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [limits, setLimits] = useState<PaymentLimits | null>(null);
  const [calculation, setCalculation] = useState<PaymentCalculation | null>(null);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [calculationError, setCalculationError] = useState('');
  const referralCode = params.get('ref') || params.get('promo');
  const rubAmount = Number(rub) || 0;
  const minPaymentRub = limits?.minAmount;
  const maxPaymentRub = limits?.maxAmount;
  const amountIsWithinLimits = minPaymentRub != null &&
    rubAmount >= minPaymentRub &&
    (maxPaymentRub == null || rubAmount <= maxPaymentRub);
  const registrationUrl = referralCode
    ? `/register?ref=${encodeURIComponent(referralCode)}`
    : '/register';
  const selectedOption = paymentOptions.find(option => option.network === selectedNetwork) ?? null;
  const selectedLiquidity = selectedOption?.liquidity ?? null;
  const hasSelectedNetworkLiquidity = selectedLiquidity != null && selectedLiquidity > 0;
  const hasEnoughSelectedNetworkLiquidity = calculation != null &&
    selectedLiquidity != null &&
    calculation.usdtAmount <= selectedLiquidity;

  useEffect(() => {
    let cancelled = false;
    getPaymentProducts()
      .then(products => {
        if (cancelled) return;
        const options = buildPaymentNetworkOptions(products);
        setPaymentOptions(options);
        setSelectedNetwork(current =>
          current && options.some(option => option.network === current)
            ? current
            : options[0]?.network ?? ''
        );
      })
      .catch(() => {
        if (!cancelled) {
          setPaymentOptions([]);
          setSelectedNetwork('');
        }
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (selectedNetwork) {
      refreshSettings(selectedNetwork);
    }
  }, [refreshSettings, selectedNetwork]);

  useEffect(() => {
    let cancelled = false;
    getPublicOrderPaymentLimits()
      .then(result => { if (!cancelled) setLimits(result); })
      .catch(() => {
        if (!cancelled) setCalculationError('Калькулятор временно недоступен. Повторите попытку позже.');
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!amountIsWithinLimits || !selectedOption) {
      setCalculation(null);
      setCalculationLoading(false);
      return;
    }

    let cancelled = false;
    setCalculationLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const result = await calculatePublicOrderPayment({ rub: rubAmount, network: selectedOption.network });
        if (!cancelled) {
          setCalculation(result);
          setCalculationError('');
        }
      } catch (err) {
        if (!cancelled) {
          setCalculation(null);
          setCalculationError(clientErrorMessage(err, 'Не удалось рассчитать сумму.'));
        }
      } finally {
        if (!cancelled) setCalculationLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [amountIsWithinLimits, rubAmount, selectedOption]);

  if (!userAuth.token && referralCode) {
    return <Navigate to={`/register?ref=${encodeURIComponent(referralCode)}`} replace />;
  }

  if (userAuth.token) return <DashboardPage />;

  return (
    <div className="landing-layout">
      <div className="landing-left">
        <div className="brand"><span className="brand-dot"></span><span className="brand-name">SBP<span>USDT</span></span></div>
        <div className="hero-orbit" aria-hidden="true">
          <span className="hero-orbit-ring hero-orbit-ring--outer" />
          <span className="hero-orbit-ring hero-orbit-ring--inner" />
          <span className="hero-orbit-core">₽</span>
          <span className="hero-orbit-node hero-orbit-node--one">₮</span>
          <span className="hero-orbit-node hero-orbit-node--two">₿</span>
        </div>
        <h1 className="landing-headline">Покупка USDT<br /><span className="accent">через СБП</span></h1>
        <p className="landing-sub">Быстрая оплата в рублях.<br />Статус платежа доступен в кабинете.</p>
        <div className="landing-features" style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={featureStyle}><span className="feature-number">01</span><span style={featureTextStyle}>Получайте USDT только через включённые сети</span></div>
          <div style={featureStyle}><span className="feature-number">02</span><span style={featureTextStyle}>Пройдите KYC перед первым платежом</span></div>
          <div style={featureStyle}><span className="feature-number">03</span><span style={featureTextStyle}>Следите за заявками в личном кабинете</span></div>
        </div>
      </div>
      <div className="landing-right">
        <Card>
          <TurnoverInfo turnover={settings?.turnover ?? settings?.liquidity ?? null} />
          <div className="public-payment-preview">
            <div>
              <h2 className="success-title public-payment-title">Рассчитайте покупку</h2>
              <p className="success-sub public-payment-sub">
                Введите сумму и узнайте, сколько USDT получите.
              </p>
            </div>
            <fieldset className="network-selector">
              <legend>Сеть получения USDT</legend>
              {paymentOptions.length > 0 ? (
                <div className="network-selector-options">
                  {paymentOptions.map(option => (
                    <label
                      key={option.network}
                      className={`network-option${selectedNetwork === option.network ? ' network-option--active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="public-network"
                        value={option.network}
                        checked={selectedNetwork === option.network}
                        onChange={() => setSelectedNetwork(option.network)}
                      />
                      <span>{option.label}</span>
                      <small>{option.description}</small>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="network-selector-hint">Нет доступных сетей получения. Повторите попытку позже.</div>
              )}
            </fieldset>
            <Input
              label="Сумма в рублях"
              type="number"
              min={minPaymentRub?.toString()}
              max={maxPaymentRub?.toString()}
              value={rub}
              onChange={event => setRub(event.target.value)}
              suffix="₽"
            />
            {minPaymentRub != null && rubAmount > 0 && rubAmount < minPaymentRub ? (
              <div className="public-payment-hint">
                Минимальная сумма оплаты {minPaymentRub.toLocaleString('ru-RU')} ₽
              </div>
            ) : null}
            {maxPaymentRub != null && rubAmount > maxPaymentRub ? (
              <div className="public-payment-hint">
                Максимальная сумма оплаты {maxPaymentRub.toLocaleString('ru-RU')} ₽
              </div>
            ) : null}
            {amountIsWithinLimits ? (
              <div className="calc-result">
                <span className="calc-label">Получите</span>
                <span className="calc-amount mono">
                  {calculationLoading || calculation == null ? '...' : `${calculation.usdtAmount.toFixed(6)} USDT`}
                </span>
              </div>
            ) : null}
            {selectedLiquidity != null && !hasSelectedNetworkLiquidity ? (
              <div className="public-payment-error">В выбранной сети сейчас нет доступной ликвидности.</div>
            ) : null}
            {hasSelectedNetworkLiquidity && calculation && !hasEnoughSelectedNetworkLiquidity ? (
              <div className="public-payment-error">Недостаточно ликвидности в сети {selectedOption?.label ?? selectedNetwork}.</div>
            ) : null}
            {calculationError ? <div className="public-payment-error">{calculationError}</div> : null}
            <Link to={registrationUrl}><Button fullWidth size="lg">Зарегистрироваться</Button></Link>
            <Link to="/login" className="public-login-link">Уже есть аккаунт? Войти</Link>
          </div>
        </Card>
        <div className="legal-info-block">
          Операции пополнения через СБП осуществляются с использованием инфраструктуры лицензированного поставщика услуг виртуальных активов Bitbanker KG (Кыргызская Республика). Прохождение идентификации (KYC) является обязательным условием использования сервиса.
        </div>
      </div>
    </div>
  );
}
